import { useState, useEffect, useMemo, useCallback } from 'react';
import { CreditCard, XCircle, AlertTriangle, CheckCircle, Clock, Plus, Edit3, Trash2, X, Send, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../../components/ui/Pagination';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 15;

export default function AdminSubscriptions() {
  const { token } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Propose modal
  const [showProposeModal, setShowProposeModal] = useState(false);
  const [proposeForm, setProposeForm] = useState({ user_id: '', name: '', amount: '25', notes: '' });
  const [clientSearch, setClientSearch] = useState('');
  const [savingPropose, setSavingPropose] = useState(false);

  // Price update modal
  const [priceModal, setPriceModal] = useState(null); // sub object | null
  const [newPrice, setNewPrice] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const paginatedSubscriptions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return subscriptions.slice(start, start + ITEMS_PER_PAGE);
  }, [subscriptions, currentPage]);

  const totalPages = Math.ceil(subscriptions.length / ITEMS_PER_PAGE);

  const fetchData = useCallback(async () => {
    try {
      const [subsRes, cancelRes, clientsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/subscriptions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/subscriptions/cancellation-requests`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/clients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      setSubscriptions(await subsRes.json());
      setCancellationRequests(await cancelRes.json());
      setClients(await clientsRes.json());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---- Propose subscription ----
  const filteredClients = useMemo(() => {
    const q = clientSearch.toLowerCase().trim();
    if (!q) return clients;
    return clients.filter((c) => {
      const bi = c.billing_info || {};
      const haystack = [c.email, bi.first_name, bi.last_name, bi.company_name].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [clients, clientSearch]);

  const handlePropose = async () => {
    if (!proposeForm.user_id) { toast.error('Sélectionnez un client'); return; }
    if (!proposeForm.name.trim()) { toast.error('Saisissez un libellé'); return; }
    const amt = parseFloat(proposeForm.amount);
    if (!(amt > 0)) { toast.error('Le prix doit être supérieur à 0'); return; }
    setSavingPropose(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/propose`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: proposeForm.user_id,
          name: proposeForm.name.trim(),
          amount: amt,
          notes: proposeForm.notes.trim(),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de la création');
      }
      toast.success('Proposition envoyée au client');
      setShowProposeModal(false);
      setProposeForm({ user_id: '', name: '', amount: '25', notes: '' });
      setClientSearch('');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPropose(false);
    }
  };

  // ---- Update price ----
  const handleUpdatePrice = async () => {
    if (!priceModal) return;
    const amt = parseFloat(newPrice);
    if (!(amt > 0)) { toast.error('Le prix doit être supérieur à 0'); return; }
    setSavingPrice(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/${priceModal.id}/price`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de la mise à jour');
      }
      toast.success('Prix mis à jour');
      setPriceModal(null);
      setNewPrice('');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPrice(false);
    }
  };

  // ---- Delete proposal ----
  const handleDeleteProposal = async (sub) => {
    if (!window.confirm(`Supprimer la proposition « ${sub.name || 'sans nom'} » pour ${sub.client_email} ?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/${sub.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Suppression impossible');
      toast.success('Proposition supprimée');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCancel = async (subId) => {
    if (!window.confirm('Annuler cet abonnement ?')) return;
    setActionLoading(subId);
    try {
      await fetch(`${API_URL}/api/admin/subscriptions/${subId}/cancel`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmCancellation = async (subId) => {
    if (!window.confirm('Confirmer l\'annulation de cet abonnement ? L\'abonnement sera annulé sans remboursement.')) return;
    setActionLoading(subId);
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/${subId}/confirm-cancellation`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        await fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectCancellation = async (subId) => {
    if (!window.confirm('Rejeter cette demande d\'annulation ? L\'abonnement restera actif.')) return;
    setActionLoading(subId);
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/${subId}/reject-cancellation`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        await fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSyncInvoices = async (sub) => {
    if (!sub.stripe_subscription_id) {
      toast.error("Cet abonnement n'a pas encore d'ID Stripe.");
      return;
    }
    if (!window.confirm(`Synchroniser les factures Stripe manquantes pour "${sub.name || sub.plan}" ? Les factures déjà présentes seront ignorées.`)) return;
    setActionLoading(sub.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/${sub.id}/sync-invoices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      toast.success(data.message);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      proposed: 'bg-blue-100 text-blue-700',
      pending: 'bg-yellow-100 text-yellow-700',
      cancellation_requested: 'bg-orange-100 text-orange-700',
      cancelled: 'bg-red-100 text-red-700',
      expired: 'bg-gray-100 text-gray-700'
    };
    const labels = { 
      active: 'Actif', 
      proposed: 'Proposé',
      pending: 'En attente', 
      cancellation_requested: 'Annulation demandée',
      cancelled: 'Annulé', 
      expired: 'Expiré' 
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return <div className="animate-pulse h-64 bg-muted rounded-xl" />;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold mb-2">Abonnements</h1>
          <p className="text-muted-foreground">{subscriptions.length} abonnement(s)</p>
        </div>
        <Button onClick={() => setShowProposeModal(true)} className="btn-primary" data-testid="propose-sub-btn">
          <Plus className="h-4 w-4 mr-2" /> Proposer un abonnement
        </Button>
      </div>

      {/* Cancellation Requests Section */}
      {cancellationRequests.length > 0 && (
        <Card className="border-orange-500/50">
          <CardHeader className="bg-orange-50 dark:bg-orange-900/20 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
              <AlertTriangle className="h-5 w-5" />
              Demandes d'annulation en attente ({cancellationRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {cancellationRequests.map((sub) => (
                <div key={sub.id} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                      <Clock className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium">{sub.client_name || sub.client_email}</p>
                      <p className="text-sm text-muted-foreground">{sub.client_email}</p>
                      {sub.cancellation_reason && (
                        <p className="text-sm mt-2 p-2 bg-muted rounded">
                          <span className="font-medium">Raison:</span> {sub.cancellation_reason}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Demande le {new Date(sub.cancellation_requested_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 md:flex-shrink-0">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => handleRejectCancellation(sub.id)}
                      disabled={actionLoading === sub.id}
                      className="text-green-600 border-green-600 hover:bg-green-50"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Rejeter
                    </Button>
                    <Button 
                      variant="destructive"
                      size="sm"
                      onClick={() => handleConfirmCancellation(sub.id)}
                      disabled={actionLoading === sub.id}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Confirmer annulation
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {subscriptions.length === 0 ? (
            <div className="p-12 text-center">
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucun abonnement</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr>
                    <th className="text-left p-4 font-medium text-muted-foreground">Client</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Nom du site</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Plan</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Début</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Fin</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Montant</th>
                    <th className="text-center p-4 font-medium text-muted-foreground">Statut</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSubscriptions.map((sub) => (
                    <tr key={sub.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{sub.client_name || '-'}</p>
                          <p className="text-sm text-muted-foreground">{sub.client_email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={sub.name ? 'font-medium' : 'text-muted-foreground italic'}>
                          {sub.name || 'Non spécifié'}
                        </span>
                      </td>
                      <td className="p-4">
                        {sub.plan === 'hosting_resend' ? 'Hébergement + Emails' : 'Hébergement'}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {sub.current_period_start 
                          ? new Date(sub.current_period_start).toLocaleDateString('fr-FR')
                          : '-'
                        }
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {sub.current_period_end 
                          ? new Date(sub.current_period_end).toLocaleDateString('fr-FR')
                          : '-'
                        }
                      </td>
                      <td className="p-4 text-right font-semibold">{Number(sub.amount).toFixed(2)}€</td>
                      <td className="p-4 text-center">
                        <StatusBadge status={sub.status} />
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {(sub.status === 'active' || sub.status === 'proposed' || sub.status === 'pending') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setPriceModal(sub); setNewPrice(String(sub.amount)); }}
                              data-testid={`edit-price-${sub.id}`}
                            >
                              <Edit3 className="h-4 w-4 mr-1" /> Prix
                            </Button>
                          )}
                          {(sub.status === 'proposed' || sub.status === 'pending') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteProposal(sub)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-${sub.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          {sub.status === 'active' && sub.stripe_subscription_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSyncInvoices(sub)}
                              disabled={actionLoading === sub.id}
                              title="Récupérer les factures manquantes depuis Stripe"
                              data-testid={`sync-invoices-${sub.id}`}
                            >
                              <RefreshCw className={`h-4 w-4 mr-1 ${actionLoading === sub.id ? 'animate-spin' : ''}`} />
                              Sync factures
                            </Button>
                          )}
                          {sub.status === 'active' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleCancel(sub.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Résilier
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={subscriptions.length}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </CardContent>
      </Card>

      {/* ============ PROPOSE MODAL ============ */}
      {showProposeModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4">
          <Card className="w-full max-w-xl my-8">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Proposer un abonnement</CardTitle>
              <button onClick={() => setShowProposeModal(false)} className="p-1 hover:bg-muted rounded">
                <X className="h-4 w-4" />
              </button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Client *</Label>
                <Input
                  placeholder="Rechercher par email ou nom…"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  data-testid="propose-client-search"
                />
                <div className="mt-2 max-h-44 overflow-y-auto border rounded-md divide-y">
                  {filteredClients.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground">Aucun client</div>
                  ) : filteredClients.slice(0, 30).map((c) => {
                    const bi = c.billing_info || {};
                    const name = [bi.first_name, bi.last_name].filter(Boolean).join(' ') || bi.company_name || '';
                    const selected = proposeForm.user_id === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setProposeForm((f) => ({ ...f, user_id: c.id }))}
                        className={`w-full text-left p-2 text-sm hover:bg-muted ${selected ? 'bg-primary/10 font-medium' : ''}`}
                        data-testid={`select-client-${c.id}`}
                      >
                        <div>{name || c.email}</div>
                        <div className="text-xs text-muted-foreground">{c.email}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Libellé (visible par le client) *</Label>
                <Input
                  value={proposeForm.name}
                  onChange={(e) => setProposeForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Ex. Hébergement site vitrine Boulangerie X"
                  data-testid="propose-name"
                />
              </div>
              <div>
                <Label>Prix mensuel (€) *</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={proposeForm.amount}
                  onChange={(e) => setProposeForm((f) => ({ ...f, amount: e.target.value }))}
                  data-testid="propose-amount"
                />
              </div>
              <div>
                <Label>Notes internes (optionnel)</Label>
                <Input
                  value={proposeForm.notes}
                  onChange={(e) => setProposeForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Ex. Inclus 2 boîtes mail + SSL + sauvegardes"
                />
                <p className="text-xs text-muted-foreground mt-1">Affichées au client sur la proposition.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowProposeModal(false)}>Annuler</Button>
                <Button onClick={handlePropose} disabled={savingPropose} className="btn-primary" data-testid="confirm-propose">
                  <Send className="h-4 w-4 mr-2" />
                  {savingPropose ? 'Envoi…' : 'Envoyer la proposition'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ PRICE MODAL ============ */}
      {priceModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Modifier le prix mensuel</CardTitle>
              <button onClick={() => setPriceModal(null)} className="p-1 hover:bg-muted rounded">
                <X className="h-4 w-4" />
              </button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <strong>{priceModal.name || 'Hébergement'}</strong>
                <div>{priceModal.client_email}</div>
                <div>Prix actuel : <strong>{Number(priceModal.amount).toFixed(2)}€/mois</strong></div>
              </div>
              <div>
                <Label>Nouveau prix mensuel (€)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  data-testid="new-price-input"
                />
                {priceModal.status === 'active' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Le changement est appliqué immédiatement sur Stripe avec un calcul au prorata sur la prochaine facture.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPriceModal(null)}>Annuler</Button>
                <Button onClick={handleUpdatePrice} disabled={savingPrice} className="btn-primary" data-testid="confirm-price">
                  {savingPrice ? 'Mise à jour…' : 'Mettre à jour'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
